/*
 * -----------------------------------------------------------------------
 *  <copyright file="view-subject-visit-template.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import AddVisitForm from '@components/admin/add-visit-form';
import AssessmentsSVTGrid from '@components/admin/assessments-svt-grid';
import CRFsSVTGrid from '@components/admin/crfs-svt-grid';
import DeleteVisitDialog from '@components/admin/delete-visit';
import DeleteVisitWarningDialog from '@components/admin/delete-visit-warning';
import EditVisitSequenceGrid from '@components/admin/edit-visit-sequence-grid';
import EditVisitPanel from '@components/admin/view-subject-visit-template/edit-visit-panel';
import { ISVTAssignedItem } from '@components/admin/visits-tab/visits-tab.interface';
import ActionDialog from '@components/core/action-dialog';
import RefreshComponent from '@components/core/refresh-component';
import StatisticsBar from '@components/core/statistics-bar';
import TwoColsSplitter from '@components/core/two-cols-splitter/index';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import { ViewSVTDialogInterface } from '@interfaces/svt.interface';
import { useBoolean, useGridCells, useTranslation } from 'hooks';
// eslint-disable-next-line max-len
import { StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit } from 'queries/generated/fragments.generated';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import VisitsTab from 'views/components/admin/visits-tab';
import { CustomDialog, DialogContent, DialogTitle } from 'views/components/core/dialog';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import NotificationService from '@utils/notification';
import { notEmpty } from '@utils/utility-helpers';
import { StudyCrfFields, StudySubjectVisitFields, StudyAssessmentFields } from '@queries/generated/fragments.generated';
import { studyVisitSchedules, studyVisitSchedulesVariables } from '@queries/generated/study-visit-schedules.generated';
import { GET_STUDY_VISIT_SCHEDULES } from '@queries/study-visit-schedules';
import './styles.scss';

const switchGridsNames = {
    visits: 'visits',
    crfs: 'crfs',
    assessments: 'assessments',
};

const ViewSubjectVisitTemplateDialog = ({
    loadedData,
    currentSchedule,
    studyId,
    onClose,
    open,
    canEdit,
    assessmentAssociation,
}: ViewSVTDialogInterface) => {
    const { t } = useTranslation();
    const { YesNoCell, MaxLengthCell } = useGridCells();
    const openDeleteVisit = useBoolean(false);
    const [currentVisit, setCurrentVisit] = useState<StudySubjectVisitFields | null | undefined>(null);
    const [currentCRF, setCurrentCRF] = useState<StudyCrfFields | null>(null);
    const [currentAssessment, setCurrentAssessment] = useState<StudyAssessmentFields | null>(null);
    const [currentGrid, setCurrentGrid] = useState<string>(switchGridsNames.visits);
    const [visits, setVisits] = useState<StudySubjectVisitFields[]>([]);
    const showNewVisit = useBoolean(false);
    const showEditSequence = useBoolean(false);
    const showEditSequenceWarning = useBoolean(false);
    const showLostDataWarning = useBoolean(false);
    const sequenceOrderChanged = useBoolean(false);
    const canDeleteVisit = useBoolean(true);
    const formChanged = useBoolean(false);

    const [loadVisitSchedules, { data }] = useLazyQuery<studyVisitSchedules, studyVisitSchedulesVariables>(
        GET_STUDY_VISIT_SCHEDULES,
    );

    useEffect(() => {
        const newVisits = loadedData?.filter(notEmpty).find((item) => item.id === currentSchedule?.id);
        if (newVisits) setVisits(newVisits?.visits?.filter(notEmpty) ?? []);
    }, [loadedData, currentSchedule?.id]);

    useEffect(() => {
        if (!open) {
            setCurrentGrid(switchGridsNames.visits);
        }
    }, [open]);

    const close = (action?: any) => {
        sequenceOrderChanged.setFalse();
        if (formChanged.value) {
            showLostDataWarning.setTrue();
        } else if (showNewVisit.value) {
            formChanged.setFalse();
            showNewVisit?.setFalse();
            onClose(action);
        } else {
            formChanged.setFalse();
            onClose(action);
        }
    };

    const closeLostDataWarning = (action: string) => {
        if (action === 'close') {
            if (showEditSequence.value) {
                sequenceOrderChanged.setFalse();
                showEditSequence.setFalse();
                showLostDataWarning.setFalse();
            } else if (showNewVisit.value) {
                showNewVisit?.setFalse();
                formChanged.setFalse();
                onClose(action);
            } else {
                formChanged.setFalse();
                showLostDataWarning.setFalse();
                onClose(action);
            }
        }
        showLostDataWarning.setFalse();
    };

    const closeSequenceEdit = () => {
        if (sequenceOrderChanged.value) {
            showLostDataWarning.setTrue();
        } else {
            showEditSequence.setFalse();
        }
    };

    const onSubmitVisitSequenceDialog = () => {
        showEditSequenceWarning.setFalse();
        showEditSequence.setTrue();
    };

    const reloadData = () => {
        loadVisitSchedules({ variables: { studyId } });
        showEditSequence.setFalse();
        sequenceOrderChanged.setFalse();
    };

    useEffect(() => {
        if (currentVisit) {
            showNewVisit.setFalse();
        }
        const isParentVisit = visits?.filter((x) => x.leadVisitId === currentVisit?.id);
        if (isParentVisit?.length) {
            canDeleteVisit.setFalse();
        } else {
            canDeleteVisit.setTrue();
        }
    }, [currentVisit, studyId]);

    const getAssignedItem = (): ISVTAssignedItem => {
        const isCrfView = currentGrid === switchGridsNames.crfs;
        const currentItem = isCrfView ? currentCRF : currentAssessment;

        return {
            id: currentItem?.id,
            recordId: currentItem?.recordId || currentItem?.id,
            name: (isCrfView ? currentCRF?.crfName : currentAssessment?.name) || '',
            isCrf: isCrfView,
        };
    };

    const closeDeleteVisitDialog = (action?: string) => {
        if (action === 'error') {
            NotificationService.showNotification(t('errorMessage'));
        } else {
            if (action === 'delete') {
                NotificationService.showNotification(t('admin:deletedVisitMessage'));
                setCurrentVisit(null);
            }
            openDeleteVisit.setFalse();
        }
    };

    const switchMenu = assessmentAssociation
        ? [
              {
                  label: t('subject:visits'),
                  value: 'visits',
                  onClick: (value: string) => {
                      setCurrentGrid(value);
                  },
                  currentValue: currentGrid,
              },
              {
                  label: t('subject:CRFs'),
                  value: 'crfs',
                  onClick: (value: string) => {
                      setCurrentGrid(value);
                  },
                  currentValue: currentGrid,
              },
          ]
        : [
              {
                  label: t('subject:visits'),
                  value: 'visits',
                  onClick: (value: string) => {
                      setCurrentGrid(value);
                  },
                  currentValue: currentGrid,
              },
              {
                  label: t('subject:CRFs'),
                  value: 'crfs',
                  onClick: (value: string) => {
                      setCurrentGrid(value);
                  },
                  currentValue: currentGrid,
              },
              {
                  label: t('subject:assessments'),
                  value: 'assessments',
                  onClick: (value: string) => {
                      setCurrentGrid(value);
                  },
                  currentValue: currentGrid,
              },
          ];

    useEffect(() => {
        if (currentVisit) {
            const updatedCurrentVisit = visits?.find(
                (visit: StudyVisitScheduleFields_StudySubjectVisit) => visit.id === currentVisit?.id,
            );
            setCurrentVisit(updatedCurrentVisit);
        }
    }, [visits]);

    const statisticsInfo = [
        {
            name: t('common:Cohorts'),
            value: currentSchedule?._cohorts.length ? currentSchedule?._cohorts : t('ra:n/a'),
        },
        {
            name: t('admin:treatmentArms'),
            value: currentSchedule?._treatmentArms.length ? currentSchedule?._treatmentArms : t('ra:n/a'),
        },
    ];

    return (
        <>
            {showEditSequenceWarning.value && (
                <ActionDialog
                    title={t('admin:editVisitSequence')}
                    onClose={() => showEditSequenceWarning.setFalse()}
                    onSubmit={onSubmitVisitSequenceDialog}
                    submitText={t('continue')}
                    className="action-dialog-small"
                >
                    <div>{t('admin:editSequenceWarningMessage')}</div>
                </ActionDialog>
            )}

            <WarningLostDataDialog
                open={showLostDataWarning.value}
                onClose={(action: string) => {
                    closeLostDataWarning(action);
                }}
            />

            {showEditSequence.value && (
                <CustomDialog
                    className="risk-details-dialog svt-sequence-dialog full-screen-dialog"
                    onClose={closeSequenceEdit}
                    aria-labelledby="assign-treatment-arms"
                    fullWidth
                    scroll="paper"
                    fullScreen
                    open={open}
                >
                    <DialogTitle id="view-subject-visit-modal-title">
                        <div className="header-part">
                            <div className="left-part">{t('admin:editVisitSequence')}</div>
                            <div className="right-part">
                                <FontAwesomeIcon
                                    id="svt-close-button"
                                    icon={['fal', 'times']}
                                    className="fa-lg close-icon-cv"
                                    onClick={closeSequenceEdit}
                                    data-testid="close-dialog"
                                />
                            </div>
                        </div>
                    </DialogTitle>
                    <DialogContent className="svt-sequence-content">
                        <EditVisitSequenceGrid
                            studyId={studyId}
                            items={visits}
                            visitScheduleId={currentSchedule?.id}
                            onSaveSequence={reloadData}
                            onClose={closeSequenceEdit}
                            orderChanged={sequenceOrderChanged}
                            columns={[
                                {
                                    label: t('common:active'),
                                    field: 'active',
                                    id: 'visit-active-visit',
                                    minWidth: 160,
                                    cells: { data: YesNoCell },
                                    filterUi: GridColumnMenuActiveFilter,
                                },
                                {
                                    label: t('admin:visitType'),
                                    field: 'type',
                                    id: 'visit-type-visit',
                                    minWidth: 170,
                                    cells: { data: MaxLengthCell(50) },
                                },
                                {
                                    label: t('subject:visitId'),
                                    field: 'visitId',
                                    id: 'visit-id-visit',
                                    minWidth: 160,
                                },
                                {
                                    label: t('subject:visitName'),
                                    field: 'visitName',
                                    id: 'visit-name-visit',
                                    minWidth: 190,
                                    cells: { data: MaxLengthCell(50) },
                                },
                                {
                                    label: t('admin:leadVisitId'),
                                    field: '_readableLeadVisitId',
                                    id: 'lead-visit-id-visit',
                                    minWidth: 190,
                                },
                            ]}
                        />
                    </DialogContent>
                </CustomDialog>
            )}
            <CustomDialog
                className="risk-details-dialog svt-dialog full-screen-dialog"
                onClose={close}
                aria-labelledby="assign-treatment-arms"
                fullWidth
                scroll="paper"
                fullScreen
                open={open}
            >
                <DialogTitle id="view-subject-visit-modal-title">
                    <div className="header-part">
                        <div className="left-part">
                            {t('admin:subjectVisitTemplateTitle')}: {currentSchedule?.name} - {currentSchedule?.version}
                        </div>
                        <div className="right-part">
                            {currentSchedule ? (
                                <p className="date-label">
                                    {' '}
                                    {t('common:updated')}{' '}
                                    {formatDateUTC_DEPRECATED(currentSchedule.updated, true, 'DD-MMM-YYYY')}
                                    {currentSchedule.updatedBy && (
                                        <span>
                                            {' '}
                                            {t('common:by')} {currentSchedule.updatedBy?.firstName}{' '}
                                            {currentSchedule.updatedBy?.lastName}
                                        </span>
                                    )}
                                </p>
                            ) : null}
                            <FontAwesomeIcon
                                id="svt-close-button"
                                icon={['fal', 'times']}
                                className="fa-lg close-icon-cv"
                                onClick={close}
                                data-testid="close-dialog"
                            />
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent
                    className={cn('svt-content', {
                        'with-info': currentSchedule?._cohorts || currentSchedule?._treatmentArms,
                    })}
                >
                    <StatisticsBar className="svt-statistics" data={statisticsInfo} />
                    <TwoColsSplitter
                        orientation="horizontal"
                        alignmentValues={[
                            {
                                min: '20%',
                                size: '50%',
                                keepMounted: true,
                            },
                            { min: '20%' },
                        ]}
                        leftCol={
                            <>
                                {currentGrid === switchGridsNames.visits && (
                                    <VisitsTab
                                        visits={visits}
                                        currentSchedule={currentSchedule}
                                        currentVisit={currentVisit}
                                        setCurrentVisit={setCurrentVisit}
                                        showNewVisit={showNewVisit}
                                        studyId={studyId}
                                        canEdit={canEdit}
                                        switchMenu={switchMenu}
                                        showEditSequenceWarning={showEditSequenceWarning}
                                        disableTools={formChanged.value}
                                    />
                                )}
                                {currentGrid === switchGridsNames.crfs && (
                                    <CRFsSVTGrid
                                        canEdit={canEdit}
                                        currentItem={currentCRF}
                                        setCurrentItem={setCurrentCRF}
                                        switchMenu={switchMenu}
                                        currentScheduleVersion={currentSchedule?.version}
                                        currentScheduleId={currentSchedule?.id}
                                        assessmentAssociation={assessmentAssociation}
                                    />
                                )}
                                {currentGrid === switchGridsNames.assessments && (
                                    <AssessmentsSVTGrid
                                        studyId={studyId}
                                        canEdit={canEdit}
                                        currentScheduleVersion={currentSchedule?.version}
                                        currentScheduleId={currentSchedule?.id}
                                        currentItem={currentAssessment}
                                        setCurrentItem={setCurrentAssessment}
                                        switchMenu={switchMenu}
                                        assessmentAssociation={assessmentAssociation}
                                    />
                                )}
                            </>
                        }
                        rightCol={
                            <>
                                {currentGrid === switchGridsNames.visits && (
                                    <>
                                        {!canDeleteVisit.value && openDeleteVisit.value && (
                                            <DeleteVisitWarningDialog
                                                open={openDeleteVisit.value}
                                                onClose={() => {
                                                    openDeleteVisit.setFalse();
                                                }}
                                            />
                                        )}
                                        {canDeleteVisit.value && openDeleteVisit.value && (
                                            <DeleteVisitDialog
                                                currentVisit={currentVisit}
                                                currentScheduleId={currentSchedule?.id}
                                                studyId={studyId}
                                                close={closeDeleteVisitDialog}
                                            />
                                        )}
                                        {!showNewVisit.value && (
                                            <EditVisitPanel
                                                canEdit={canEdit}
                                                visits={visits}
                                                openDeleteVisit={openDeleteVisit.setTrue}
                                                currentVisit={currentVisit}
                                                currentScheduleId={currentSchedule?.id}
                                                currentScheduleVersion={currentSchedule?.version}
                                                studyId={studyId}
                                                formChanged={formChanged}
                                                assessmentAssociation={assessmentAssociation}
                                            />
                                        )}
                                        {showNewVisit.value && (
                                            <AddVisitForm
                                                visits={visits}
                                                currentScheduleId={currentSchedule?.id}
                                                studyId={studyId}
                                                showNewVisit={showNewVisit}
                                                formChanged={formChanged}
                                                close={() => {
                                                    showNewVisit.setFalse();
                                                }}
                                                canEdit={canEdit}
                                            />
                                        )}
                                    </>
                                )}
                                {(currentGrid === switchGridsNames.assessments ||
                                    currentGrid === switchGridsNames.crfs) && (
                                    <RefreshComponent
                                        dataChange={
                                            currentGrid === switchGridsNames.crfs
                                                ? currentCRF?.id
                                                : currentAssessment?.id
                                        }
                                    >
                                        <VisitsTab
                                            visits={visits}
                                            currentSchedule={currentSchedule ?? null}
                                            currentVisit={currentVisit}
                                            setCurrentVisit={setCurrentVisit}
                                            showNewVisit={showNewVisit}
                                            studyId={studyId}
                                            canEdit={canEdit}
                                            switchMenu={switchMenu}
                                            assignedItem={getAssignedItem()}
                                        />
                                    </RefreshComponent>
                                )}
                            </>
                        }
                    />
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default ViewSubjectVisitTemplateDialog;
